/**
 * One filter button over the observations table. Unset it is dashed ("+ Type");
 * set it is solid and shows its value. Clicking opens a checkbox list with a
 * count per value (radios when only one value can hold), an optional search
 * box, and Clear. Escape or a click outside closes it. The list floats in a
 * portal, so a dialog around it cannot clip it.
 */

import { useId, useRef, useState } from "react";
import { Floating } from "@/components/ui/floating";
import { formatCount } from "@/lib/format";

export interface FilterChoice {
  value: string;
  label: string;
  /** Left out when there is nothing to count (a fixed list). */
  count?: number;
}

export function FilterButton({
  label,
  choices,
  chosen,
  onChange,
  single = false,
  search = false,
}: {
  label: string;
  choices: FilterChoice[];
  chosen: string[];
  onChange: (values: string[]) => void;
  /** At most one value (radios). */
  single?: boolean;
  search?: boolean;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const on = chosen.length > 0;
  const names = chosen.map((v) => choices.find((c) => c.value === v)?.label ?? v);
  const shown = names.length > 2 ? `${names.length} selected` : names.join(", ");
  const q = query.trim().toLowerCase();
  const listed = q ? choices.filter((c) => c.label.toLowerCase().includes(q)) : choices;

  // Closing from inside the list hands focus back to the button, not the page.
  const close = () => (setOpen(false), button.current?.focus());
  const toggle = (value: string, checked: boolean) =>
    onChange(single ? [value] : checked ? [...chosen, value] : chosen.filter((v) => v !== value));

  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) (e.stopPropagation(), close());
      }}
    >
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        title={on ? `${label}: ${names.join(", ")}` : undefined}
        className={`flex max-w-[260px] min-w-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs ${
          on ? "border-ink bg-selected text-ink" : "border-dashed border-line text-muted hover:text-ink"
        }`}
        onClick={() => (setQuery(""), setOpen(!open))}
      >
        <span className="shrink-0 font-semibold whitespace-nowrap">{on ? label : `+ ${label}`}</span>
        {on && <span className="min-w-0 truncate">{shown}</span>}
      </button>
      {open && (
        <Floating
          anchor={root}
          onDismiss={() => setOpen(false)}
          id={id}
          role="group"
          aria-label={`${label} filter`}
          onKeyDown={(e) => {
            if (e.key === "Escape") (e.stopPropagation(), close());
          }}
          className="flex w-64 flex-col gap-0.5 p-1.5"
        >
          {search && (
            <input
              autoFocus
              type="search"
              aria-label={`Search ${label.toLowerCase()}`}
              placeholder="Search…"
              className="inp mb-1 h-8 text-[13px]"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <div className="flex max-h-64 flex-col overflow-auto">
            {listed.length === 0 && <span className="px-2 py-1.5 text-[13px] text-muted">No matches</span>}
            {listed.map((c, i) => (
              <label
                key={c.value}
                className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-hover"
              >
                <input
                  // Without a search box, the first choice takes focus so the keyboard can reach the list.
                  autoFocus={!search && i === 0}
                  type={single ? "radio" : "checkbox"}
                  name={id}
                  className="size-3.5 accent-ink"
                  checked={chosen.includes(c.value)}
                  onChange={(e) => toggle(c.value, e.target.checked)}
                />
                <span className="truncate">{c.label}</span>
                {c.count !== undefined && (
                  <span className="text-xs text-faint tabular-nums">{formatCount(c.count)}</span>
                )}
              </label>
            ))}
          </div>
          <div className="mt-1 flex justify-between border-t border-line-soft px-1 pt-1">
            <button type="button" className="text-xs text-link" onClick={() => onChange([])}>
              Clear
            </button>
            <button type="button" className="text-xs text-link" onClick={close}>
              Done
            </button>
          </div>
        </Floating>
      )}
    </div>
  );
}
